import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { driveClient } from '../lib/google-drive';
import { calendarClient } from '../lib/google-calendar';
import { getAccessToken } from '../lib/firebase';
import type { DriveFile, DriveFolder } from '../lib/google-drive';

interface DriveState {
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;
}

export const useDrive = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [driveState, setDriveState] = useState<DriveState>({
    isInitialized: false,
    isInitializing: false,
    error: null,
  });

  useEffect(() => {
    const initializeDrive = async () => {
      if (!user) {
        setDriveState({
          isInitialized: false,
          isInitializing: false,
          error: null,
        });
        return;
      }

      // Get the OAuth access token (not Firebase ID token)
      try {
        const accessToken = await getAccessToken();

        if (!accessToken) {
          console.error('[useDrive] No OAuth access token found - user needs to sign in again');
          setDriveState({
            isInitialized: false,
            isInitializing: false,
            error: 'No Google Drive access token found. Please sign in again.',
          });

          // Sign out to clear invalid session and redirect immediately
          await signOut();
          navigate('/login', {
            replace: true,
            state: { message: 'Please sign in again to grant Google Drive permissions.' }
          });
          return;
        }

        setDriveState((prev) => ({ ...prev, isInitializing: true, error: null }));

        // Initialize both Drive and Calendar clients with the OAuth token
        driveClient.initialize(accessToken);
        calendarClient.initialize(accessToken);

        setDriveState({
          isInitialized: true,
          isInitializing: false,
          error: null,
        });
      } catch (error) {
        console.error('[useDrive] Failed to initialize Drive client:', error);
        setDriveState({
          isInitialized: false,
          isInitializing: false,
          error: error instanceof Error ? error.message : 'Failed to initialize Drive',
        });
      }
    };

    initializeDrive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only re-run when user changes (signOut and navigate are stable)

  /**
   * Create a folder in Google Drive
   */
  const createFolder = async (name: string, parentId?: string): Promise<DriveFolder> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.createFolder(name, parentId);
  };

  /**
   * Upload a file to Google Drive
   */
  const uploadFile = async (
    fileName: string,
    content: string | object,
    folderId?: string
  ): Promise<DriveFile> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.uploadFile(fileName, content, folderId);
  };

  /**
   * Update an existing file in Google Drive
   */
  const updateFile = async (fileId: string, content: string | object): Promise<DriveFile> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.updateFile(fileId, content);
  };

  /**
   * Download a file from Google Drive
   */
  const downloadFile = async (fileId: string): Promise<string> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.downloadFile(fileId);
  };

  /**
   * List files in a folder
   */
  const listFiles = async (folderId?: string): Promise<DriveFile[]> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.listFiles(folderId);
  };

  /**
   * Delete a file from Google Drive
   */
  const deleteFile = async (fileId: string): Promise<void> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.deleteFile(fileId);
  };

  /**
   * Find a file by name
   */
  const findFile = async (fileName: string, folderId?: string): Promise<DriveFile | null> => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.findFile(fileName, folderId);
  };

  /**
   * Initialize NIDOnote folder structure
   */
  const initializeFolderStructure = async () => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.initializeFolderStructure();
  };

  /**
   * Get or create a space folder structure
   */
  const getOrCreateSpaceFolder = async (spaceId: string) => {
    if (!driveState.isInitialized) {
      throw new Error('Drive client not initialized');
    }
    return driveClient.getOrCreateSpaceFolder(spaceId);
  };

  return {
    isInitialized: driveState.isInitialized,
    isInitializing: driveState.isInitializing,
    error: driveState.error,
    createFolder,
    uploadFile,
    updateFile,
    downloadFile,
    listFiles,
    deleteFile,
    findFile,
    initializeFolderStructure,
    getOrCreateSpaceFolder,
  };
};
